/* Live CMS ticket prices. Fetches /api/cms/ticket-events once and returns
   a formatter so any page can show live admin-edited prices.

   Usage:
     const { priceFor } = useTicketPrices({ "aus-day-2026-day": {adult: 145, child: 70} });
     <span>{priceFor("aus-day-2026-day", "adult")}</span>  // "$145"

   `fallback` (optional) is the per-key default used until CMS responds or if
   the API call fails. Pass it so the first paint never shows "$0" or empty.
*/
import { useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
let CACHE = null; // module-level cache so multiple pages don't refetch on nav

export function formatPrice(n) {
 if (n == null || Number.isNaN(Number(n))) return "";
 const num = Number(n);
 const hasCents = num % 1 !== 0;
 return `$${num.toLocaleString("en-AU", { minimumFractionDigits: hasCents ? 2 : 0 })}`;
}

export default function useTicketPrices(fallback = {}) {
 const [prices, setPrices] = useState(() => CACHE || fallback);

 useEffect(() => {
  if (CACHE) { setPrices(CACHE); return; }
  let cancelled = false;
  fetch(`${API}/api/cms/ticket-events`)
   .then((r) => r.ok ? r.json() : null)
   .then((data) => {
    if (cancelled || !data || !data.events) return;
    const map = {};
    data.events.forEach((e) => { map[e.key] = { adult: e.adult, child: e.child, name: e.name }; });
    CACHE = map;
    setPrices(map);
   })
   .catch(() => { /* keep fallback */ });
  return () => { cancelled = true; };
 }, []);

 const priceFor = (key, kind = "adult") => {
  const live = prices && prices[key];
  if (live && live[kind] != null) return formatPrice(live[kind]);
  const fb = fallback[key];
  if (fb && fb[kind] != null) return formatPrice(fb[kind]);
  return "";
 };

 return { prices, priceFor };
}
